import { ReadOutlined } from '@ant-design/icons';
import {
  Button,
  Empty,
  Flex,
  Select,
  Spin,
  Table,
  TableProps,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Server } from '../../../api/server-index';
import { AuthContext } from '../../../context/auth-context';
import { BuildingDto } from '../../../types/dto/building.dto';
import { Location } from '../../../types/enum/location';
import { UserRoles } from '../../../types/enum/user-roles';
import { HasRoles } from '../../../utils/has-roles';
import MainTitle from '../../common/titles/main-title.component';

const Buildings = () => {
  const { t } = useTranslation('buildings');
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [buildings, setBuildings] = useState<BuildingDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const canEditLocation = HasRoles([UserRoles.ADMINISTRATOR], user);

  const fetchBuildings = useCallback(async () => {
    setLoading(true);
    const response = await Server.Buildings.getBuildings();
    if (Array.isArray(response)) {
      setBuildings(response);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBuildings();
  }, [fetchBuildings]);

  const handleLocationChange = async (
    buildingId: string,
    location: Location
  ) => {
    const response = await Server.Buildings.updateBuildingLocation(
      buildingId,
      location
    );
    if (response.status === 200) {
      notification.success({ message: t('update.success') });
      setBuildings((prev) =>
        prev.map((building) =>
          building.id === buildingId ? { ...building, location } : building
        )
      );
    }
  };

  const columns: TableProps<BuildingDto>['columns'] = [
    {
      key: 'name',
      title: t('columns.name'),
      dataIndex: 'name',
    },
    {
      key: 'address',
      title: t('columns.address'),
      dataIndex: 'address',
      render: (address: string) => address || '-',
    },
    {
      key: 'isActive',
      title: t('columns.active'),
      dataIndex: 'isActive',
      width: 120,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? t('active.yes') : t('active.no')}
        </Tag>
      ),
    },
    {
      key: 'location',
      title: t('columns.location'),
      dataIndex: 'location',
      width: 180,
      render: (location: Location | undefined, building) =>
        canEditLocation ? (
          <Select
            allowClear={false}
            className='w-100'
            placeholder={t('location_placeholder')}
            value={location}
            options={Object.values(Location).map((option) => ({
              label: t(`locations.${option}`),
              value: option,
            }))}
            onChange={(value: Location) =>
              handleLocationChange(building.id, value)
            }
          />
        ) : location ? (
          <span>{t(`locations.${location}`)}</span>
        ) : (
          '-'
        ),
    },
    {
      key: 'details',
      title: t('columns.details'),
      dataIndex: 'id',
      width: 80,
      render: (id) => (
        <Flex gap={12}>
          <Tooltip title={t('classrooms.tooltip')}>
            <Link to={`classrooms/${id}?${searchParams.toString()}`}>
              <Button>
                <ReadOutlined />
              </Button>
            </Link>
          </Tooltip>
        </Flex>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div ref={tableRef}></div>
      <Flex align='center' gap={12}>
        <MainTitle text={t('title')} />
      </Flex>
      <Table
        className='mb-2'
        columns={columns}
        dataSource={buildings}
        scroll={{
          x: 800,
        }}
        rowKey={(val) => val.id}
        pagination={{
          hideOnSinglePage: true,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('common:no_data')}
            />
          ),
        }}
      />
    </Spin>
  );
};

export default Buildings;
